import { useGetAllEmployeeGroups, useGetAllPersons, useGetPersonsEmployeeGroupSummary } from '@hr/api/endpoints'
import { LoadingContainer } from '@hr/common/loaders/loadingcontainer'
import { average, localized } from '@hr/common/utils'
import { useTranslation } from 'react-i18next'
import styles from './Card.module.css'
import { SalaryGapMeter } from './SalaryGapMeter'
import { determineSalary } from './SalaryLevelsGrouped'

export function PayGapsGrouped() {
	const { t } = useTranslation()

	const { data: groups } = useGetAllEmployeeGroups() // determine active employee group status
	const { data: groupPersons } = useGetPersonsEmployeeGroupSummary() // detemine which group a person belongs
	const { data: persons } = useGetAllPersons() // determine person employed status

	const countDistribution = () => {
		const groupMap = {}
		groups.forEach((group) => {
			if (group.active) {
				groupMap[group.id] = group
				group.women = []
				group.men = []
			}
		})

		const personGroupMap = {}
		groupPersons.forEach((person) => {
			personGroupMap[person.personId] = person.employeeGroupId
		})

		persons.forEach((person) => {
			const status = person.employmentDetails?.personStatus
			if ((status === 'EMPLOYED' || !status) && person.socSec) {
				const groupId = personGroupMap[person.personId]
				const group = groupMap[groupId]
				const { salaryDetails } = person
				if (group && salaryDetails) {
					const salary = determineSalary(salaryDetails)
					if (salary) {
						const g = (person.gender || '').trim().toUpperCase()
						if (g === 'M') group.men.push(salary)
						else if (g === 'N') group.women.push(salary)
					}
				}
			}
		})

		const statusCount = {
			OK: 0,
			Notice: 0,
			Action: 0,
		}

		// Calculate averages and salary difference percentage for the active employee groups
		Object.values(groupMap).forEach((group) => {
			group.menAverage = average(group.men) || 0
			group.womenAverage = average(group.women) || 0
			group.salaryDiff =
				(Math.abs(group.menAverage - group.womenAverage) / Math.max(group.menAverage, group.womenAverage)) * 100

			if (Math.min(group.menAverage, group.womenAverage) === 0) {
				group.notComparable = true
				group.salaryStatus = 'OK'
				++statusCount.OK
			} else {
				/*if (group.salaryDiff > 10) {
				group.salaryStatus = 'Action'
				++statusCount.Action} else*/
				if (group.salaryDiff >= 5) {
					group.salaryStatus = 'Notice'
					++statusCount.Notice
				} else {
					group.salaryStatus = 'OK'
					++statusCount.OK
				}
			}
		})

		return (
			<>
				<h4 className={styles.cardHeader}>{t('PayGapsGrouped', 'Palkkaeroindikaattori työntekijäryhmittäin')}</h4>

				<div
					style={{
						display: 'flex',
						width: '100%',
						whiteSpace: 'nowrap',
						justifyContent: 'space-around',
						gap: '12px',
						marginTop: '1rem',
					}}
				>
					{!!statusCount.OK && <StatusNotice salaryStatus={'OK'} amount={statusCount.OK} />}
					{!!statusCount.Notice && <StatusNotice salaryStatus={'Notice'} amount={statusCount.Notice} />}
					{!!statusCount.Action && <StatusNotice salaryStatus={'Action'} amount={statusCount.Action} />}
				</div>

				<table style={{ width: '100%', marginTop: '2em' }}>
					<thead>
						<tr style={{ borderBottom: '1px solid #eee' }}>
							<th>{t('EmployeeGroup', 'Työntekijäryhmä')}</th>
							<th>{t('PayGapPercentage', 'Palkkaero %')}</th>
							<th>{t('Status', 'Tila')}</th>
							<th>{t('Visualization', 'Visualisointi')}</th>
						</tr>
					</thead>
					<tbody>
						{Object.values(groupMap).map((group) => (
							<tr
								key={group.id}
								style={{ borderBottom: '1px solid #eee' }}
								className="padded"
								title={
									group.menAverage > group.womenAverage
										? t('AverageSalaryHigherMen', 'Miesten keskipalkka korkeampi')
										: group.womenAverage > group.menAverage &&
											t('AverageSalaryHigherWomen', 'Naisten keskipalkka korkeampi')
								}
							>
								<td>
									<span className={styles.groupBadge} style={{ background: group.colour }} title={group.groupCode}>
										{group.name}
									</span>
								</td>
								<td>
									{group.notComparable
										? t('NotComparable', 'Ei vertailtavissa')
										: `${localized.numberFormatFraction1.format(group.salaryDiff)} %`}
								</td>
								<td>
									<StatusBadge salaryStatus={group.salaryStatus} />
								</td>
								<td>{!group.notComparable && <SalaryGapMeter value={group.salaryDiff / 100} />}</td>
							</tr>
						))}
					</tbody>
				</table>
			</>
		)
	}

	// Upper pay gap notice
	const StatusNotice = ({ salaryStatus, amount }) => {
		const status = {}
		switch (salaryStatus) {
			case 'Action':
				status.color = 'var(--color-danger)'
				status.header = t('PayGapMore10', 'Yli 10 % palkkaero')
				status.label = `${t('DoActions', 'Suorita toimenpiteitä')}!`
				break
			case 'Notice':
				status.color = 'var(--color-warning)'
				status.header = t('PayGapMore5', 'Yli 5 % palkkaero')
				status.label = `${t('Notice2', 'Huomioi')}!`
				break
			default:
				status.color = 'var(--color-success)'
				status.header = t('PayGapLess5', 'Alle 5 % palkkaero')
				status.label = t('NoActions', 'Ei toimenpiteitä')
		}

		return (
			<div
				style={{
					borderColor: status.color,
					borderWidth: 2,
					borderStyle: 'solid',
					borderRadius: 8,
					padding: '.3rem',
					display: 'flex',
					width: '100%',
					whiteSpace: 'nowrap',
					justifyContent: 'space-around',
					alignItems: 'center',
					gap: '12px',
				}}
			>
				<div style={{ color: status.color }}>⬤</div>
				<div>
					<div style={{ fontWeight: 700 }}>{status.header}</div>
					<div style={{ opacity: 0.7 }}>{status.label}</div>
				</div>
				<div style={{ alignSelf: 'start', flex: 1, textAlign: 'right', marginTop: '-.2rem', marginRight: '1rem' }}>
					<b className={styles.bigFigure}>{amount}</b> {amount === 1 ? t('group', 'ryhmä') : t('groups', 'ryhmää')}
				</div>
			</div>
		)
	}

	// Badge on pay gap row
	const StatusBadge = ({ salaryStatus }) => {
		const status = {}
		switch (salaryStatus) {
			case 'Action':
				status.color = 'var(--color-danger)'
				status.label = t('Action2', 'Toimenpide')
				break
			case 'Notice':
				status.color = 'var(--color-warning)'
				status.label = t('Notice', 'Huomio')
				break
			default:
				status.color = 'var(--color-success)'
				status.label = 'OK'
		}

		return (
			<span
				style={{
					color: status.color,
					borderColor: status.color,
					borderWidth: 1.5,
					borderStyle: 'solid',
					borderRadius: 25,
					padding: '.4rem',
				}}
			>
				● {status.label}
			</span>
		)
	}

	return groups && groupPersons && persons ? countDistribution() : <LoadingContainer />
}
